'use client';

import { Typography } from '@mui/material';
import { FC, useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts';
import { PATHS, COLORS } from '@/constants';
import { TFunction, IHousingObjectResponse } from '@/types';
import { useTranslations } from 'next-intl';
import { useAddToWatchList, useDeleteFromWatchList } from '@/queris';
import { showToast } from '@/utils/showToast';
import { useWatchlistStatus } from '@/queris/useWatchlistStatus';
import { setWatchlistStatus } from '@/queris/watchlistCache';

const WatchListToast: FC<{ t: TFunction; isProject?: boolean }> = ({ t, isProject }) => (
  <Typography lineHeight="155%" fontSize={16} fontWeight={500}>
    {isProject ? t('watchList.message.projectText1') : t('watchList.message.text1')}
    <a href={`/${PATHS.PROFILE}/${PATHS.WATCH_LIST}`}>
      <span
        style={{
          color: COLORS['primary/800'],
          textDecoration: 'underline',
          textUnderlineOffset: 2,
        }}
      >
        {`${t('watchList.message.text2')}`}
      </span>
    </a>
    {`${t('watchList.message.text3')}.`}
  </Typography>
);

/**
 * Single hook for all card watchlist logic.
 * - Seeds the shared cache on mount if this id is not yet present.
 * - Reads inWatchlist reactively from the shared cache.
 * - Handles add/remove mutations with optimistic updates.
 */
export const useWatchlistCard = (data: IHousingObjectResponse, isProject?: boolean) => {
  const t = useTranslations();
  const queryClient = useQueryClient();
  const { login, isAuthenticated } = useAuth();

  const id = String(data.id);

  // Seed cache on mount only if this id is not already tracked
  useEffect(() => {
    const cache = queryClient.getQueryData<Record<string, boolean>>(['watchlist-status']);
    if (cache?.[id] === undefined) {
      setWatchlistStatus(queryClient, id, data.inWatchlist);
    }
  }, []);

  const { data: watchlistStatus } = useWatchlistStatus();
  const inWatchlist = watchlistStatus?.[id] ?? data.inWatchlist;

  const addMutation = useAddToWatchList(() => {
    showToast.success(<WatchListToast t={t} isProject={isProject} />);
  });
  const deleteMutation = useDeleteFromWatchList();

  const isLoading = addMutation.isPending || deleteMutation.isPending;

  const toggleWatch = useCallback(() => {
    if (!isAuthenticated) return login();
    if (isLoading) return;
    if (inWatchlist) {
      deleteMutation.mutate({ id });
    } else {
      addMutation.mutate({ id, isProject });
    }
  }, [id, isLoading, inWatchlist, isProject, addMutation, deleteMutation]);

  return { inWatchlist, toggleWatch, isLoading };
};
